import { Box, ButtonBase, Dialog, Fab, Stack, Tooltip, useMediaQuery } from '@mui/material';
import { isMobile } from 'react-device-detect';

import { useCallback, useRef, useState } from 'react';
import Slider from 'react-slick';
import { toast } from 'react-toastify';
import { downloadAllImages, downloadImage } from '@/components/asset/helper';
import { customStyles, getPreviewSettings, thumbnailsSettings } from './config';
import { getVersion } from '@/components/util';

type Props = {
  open: boolean;
  images: string[];
  index: number;
  tour: any;
  onClose: () => void;
};

export default function GalleryDrawer({ open, images, index, tour, onClose }: Props) {
  const isMobileLandscape = useMediaQuery('(orientation: landscape)') && isMobile;
  const previewRef = useRef(null);
  const [previewIndex, setPreviewIndex] = useState(index);

  const previewSettings = getPreviewSettings({
    afterChange: (i) => setPreviewIndex(i),
    initialSlide: index,
  });

  const downloadCurrentImage = (url: string) => () => {
    toast.promise(
      (async () => {
        await downloadImage(url, tour);
      })(),
      {
        pending: 'Preparing image for download...',
        success: 'Download complete!',
        error: 'Download failed!',
      },
    );
  };

  const downloadAll = useCallback(() => {
    toast.promise(
      (async () => {
        await downloadAllImages(images, tour);
      })(),
      {
        pending: 'Preparing images for download...',
        success: 'Download complete!',
        error: 'Download failed!',
      },
    );
  }, [images, tour]);

  const handleThumbnailClick = (idx: number) => () => {
    previewRef.current.slickGoTo(idx);
  };

  return (
    <Dialog
      className="lightbox"
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        style: {
          backgroundColor: '#000',
          boxShadow: 'none',
          overflow: 'hidden',
          justifyContent: 'center',
        },
      }}
    >
      <Fab
        size="small"
        onClick={onClose}
        sx={{
          background: 'white',
          boxShadow: 'none',
          position: 'fixed',
          top: '15px',
          right: '15px',
          zIndex: 999,
        }}
      >
        <i className="fa-regular fa-times" style={{ fontSize: '20px' }} />
      </Fab>

      <Stack
        direction="column"
        spacing={4}
        justifyContent={isMobileLandscape ? 'center' : 'space-between'}
        sx={customStyles}
      >
        {!isMobileLandscape && <Box />}
        <Box
          className="preview"
          position="relative"
          sx={{
            maxHeight: isMobileLandscape ? '100vh' : '70vh',
            '.slick-track': {
              height: '100%',
              display: 'flex',
            },
            '.slick-track .slick-slide': {
              display: 'flex',
              height: 'auto',
              alignItems: 'center',
              justifyContent: 'center',
            },
          }}
        >
          <Slider ref={previewRef} {...previewSettings}>
            {images.map((img, idx) => (
              <img key={idx} src={img} alt="preview" />
            ))}
          </Slider>

          {tour.client && tour.client.watermark && (
            <Box
              component="img"
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 99,
                opacity: 0.5,
                width: '200px',
                maxWidth: '30vw',
                maxHeight: '50vh',
                objectFit: 'contain',
              }}
              src={`${process.env.NEXT_PUBLIC_CDN}/clients/${
                tour.client.id
              }/watermark.png${getVersion(tour.client.updatedAt)}`}
            />
          )}
          {tour.client?.logo && (
            <Box
              component="img"
              sx={{
                position: 'absolute',
                top: '20px',
                left: '20px',
                zIndex: 99,
                width: tour.client?.widthLogo ? '250px' : '150px',
                maxWidth: '20vw',
                objectFit: 'contain',
              }}
              src={`${process.env.NEXT_PUBLIC_CDN}/clients/${tour.client.id}/logo.png${getVersion(
                tour.client.updatedAt,
              )}`}
            />
          )}
          <Stack
            zIndex={999}
            direction="row"
            sx={{
              position: 'absolute',
              bottom: 10,
              right: 25,
            }}
            spacing={1}
          >
            <Tooltip title="Download all">
              <Fab
                size="small"
                onClick={downloadAll}
                sx={{ background: 'white', boxShadow: 'none' }}
              >
                <i className="fa-regular fa-folder-arrow-down" style={{ fontSize: '20px' }} />
              </Fab>
            </Tooltip>
            <Tooltip title="Download current">
              <Fab
                size="small"
                onClick={downloadCurrentImage(images[previewIndex])}
                sx={{ background: 'white', boxShadow: 'none' }}
              >
                <i className="fa-regular fa-file-arrow-down" style={{ fontSize: '20px' }} />
              </Fab>
            </Tooltip>
          </Stack>
        </Box>
        {!isMobileLandscape && (
          <Box
            className="thumbs"
            sx={{
              paddingX: 2,
            }}
          >
            <Slider {...thumbnailsSettings}>
              {images.map((i, idx) => (
                <ButtonBase
                  key={idx}
                  onClick={handleThumbnailClick(idx)}
                  sx={{
                    height: '100px',
                    backgroundImage: `url(${i})`,
                    backgroundSize: 'cover',
                    backgroundOrigin: 'padding-box',
                    paddingX: 1,
                    borderRadius: '5px',
                    overflow: 'hidden',
                    opacity: idx === previewIndex ? '.6' : 1,
                  }}
                />
              ))}
            </Slider>
          </Box>
        )}
      </Stack>
    </Dialog>
  );
}
